import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Clapperboard, Trash2, Pencil, Play, Instagram, FileText, Film, Check, Share2, Send, Mail, MessageSquare, Square, Copy, Rocket, Download } from "lucide-react";
import { toast } from "sonner";
import { slidesConfig, slidesProjects, slidesDelete, slidesCancel, slidesDuplicate, slidesPushLive, promoPostReel, promoShare, createPost } from "@/lib/api";
import SlidesEditor from "@/components/slides/SlidesEditor";

const fmtDur = (s) => (s ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : "");

/** Manage → Marketing AI → Slides: AI slides video studio (full page, no pop-ups). */
export default function SlidesTab({ userRecordId, isAdmin = false }) {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | project
  const [busyId, setBusyId] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([slidesConfig(userRecordId), slidesProjects(userRecordId)]);
      setCfg(c);
      setItems(p.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the Slides studio");
    } finally {
      setLoading(false);
    }
  }, [userRecordId]);
  useEffect(() => { load(); }, [load]);

  // Poll while something renders
  useEffect(() => {
    if (!items.some((p) => p.status === "rendering")) return undefined;
    const t = setInterval(() => slidesProjects(userRecordId).then((p) => setItems(p.items || [])).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [items, userRecordId]);

  async function remove(p) {
    setBusyId(p.id);
    try { await slidesDelete(userRecordId, p.id); setItems((arr) => arr.filter((x) => x.id !== p.id)); toast.success("Project deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    finally { setBusyId(""); }
  }
  const liveUrl = (cfg?.live_url || "").replace(/\/$/, "");
  const canPushLive = !!liveUrl && window.location.origin !== liveUrl;   // only from the preview/dev portal
  async function duplicate(p) {
    setBusyId(p.id);
    try { const r = await slidesDuplicate(userRecordId, p.id); setItems((arr) => [r.project, ...arr]); toast.success(`Duplicated as “${r.project.title}” — opening it`); setEditing(r.project); setSavedAt(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate"); }
    finally { setBusyId(""); }
  }
  async function pushLive(p) {
    if (!window.confirm(`Push “${p.title}” to the live portal? It will appear there in Slides (editable) and in the Media library — pushing again just updates it.`)) return;
    setBusyId(p.id);
    try {
      const r = await slidesPushLive(userRecordId, p.id);
      toast.success("Pushed to live", { action: { label: "Open live Slides", onClick: () => window.open(r.open_url, "_blank") } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
    finally { setBusyId(""); }
  }
  async function cancelRender(p) {
    if (!window.confirm("Stop this render? Your slides and script stay saved as a draft and the points are refunded.")) return;
    setBusyId(p.id);
    try {
      const r = await slidesCancel(userRecordId, p.id);
      setItems((arr) => arr.map((x) => (x.id === p.id ? r.project : x)));
      toast.success("Render stopped — saved as a draft");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not stop the render"); }
    finally { setBusyId(""); }
  }
  async function reel(p) {
    if (!p.media_video_id) return;
    setBusyId(p.id);
    try { await promoPostReel(userRecordId, p.media_video_id); toast.success("Posting to Instagram as a Reel — check the Media tab for status"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Instagram is not connected — connect it in Settings"); }
    finally { setBusyId(""); }
  }
  async function shareToPost(p) {
    if (!p.airtable_post_id) return toast.error("This video isn't in your Media library yet");
    setBusyId(p.id);
    try {
      const caption = (p.script || (p.slides || []).map((s) => s.script).filter(Boolean).join(" ")).slice(0, 280);
      const r = await createPost({ user_record_id: userRecordId, title: p.title || "My video", caption, video_ids: [p.airtable_post_id] });
      toast.success("Post created — opening it in Posts so you can push it to your blog, home page or Instagram");
      window.dispatchEvent(new CustomEvent("marketing:sub", { detail: { sub: "posts", post: r.post } }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the post"); }
    finally { setBusyId(""); }
  }
  const sendToCrm = (p) => navigate(`/app/contacts?video=${encodeURIComponent(p.media_video_id)}`);
  async function quickShare(p, channel) {   // admins have no dealer CRM → open the mail / SMS app with the link pre-filled
    try {
      const s = await promoShare(userRecordId, p.media_video_id);
      window.location.href = channel === "email" ? `mailto:?subject=${encodeURIComponent(s.subject)}&body=${encodeURIComponent(s.text)}` : `sms:?&body=${encodeURIComponent(s.text)}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
  }

  const hero = (
    <div className="rounded-2xl bg-gradient-to-br from-[#0b2545] to-[#13315c] text-white p-5 sm:p-6 relative overflow-hidden">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#c9a34a]/20 blur-2xl" />
      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Clapperboard size={18} className="text-[#c9a34a]" /> AI Slides Video Studio</h2>
          <p className="text-sm text-white/75 mt-1 max-w-xl">Upload slides → AI writes the script → Melissa narrates → add music → generate a Reel, square or landscape video → save & post.</p>
          {cfg && !cfg.is_admin && cfg.points && (
            <p className="text-xs text-white/60 mt-2" data-testid="slides-points">AI points: <b className="text-white">{cfg.points.balance}</b> · video {cfg.costs.render} pts · script {cfg.costs.script} pt · AI music {cfg.costs.music_ai} pts</p>
          )}
        </div>
        <button type="button" disabled={!cfg} onClick={() => { setEditing({}); setSavedAt(null); }} className="rounded-xl bg-[#c9a34a] text-[#0b2545] font-bold text-sm px-4 h-10 inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60" data-testid="slides-new">
          <Plus size={15} /> New video
        </button>
      </div>
    </div>
  );
  const drafts = items.filter((p) => p.status !== "ready" && p.status !== "rendering" && p.id !== editing?.id);

  if (editing) {
    return (
      <div data-testid="slides-tab" className="space-y-4">
        {hero}
        {savedAt && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm px-4 py-2.5 flex items-center gap-2" data-testid="slides-saved-banner">
            <Check size={15} /> Draft saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} — you can come back to it anytime from the Slides list.
          </div>
        )}
        {drafts.length > 0 && (
          <div className="rounded-2xl bg-white border border-zinc-200 p-3" data-testid="slides-drafts-strip">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Your saved drafts — reuse or continue editing</div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {drafts.map((p) => (
                <button key={p.id} type="button" onClick={() => { setEditing(p); setSavedAt(null); }} className="shrink-0 w-40 rounded-xl border border-zinc-200 hover:border-violet-400 overflow-hidden text-left bg-zinc-50" data-testid={`slides-draft-${p.id}`}>
                  <div className="h-20 bg-zinc-200">{p.slides?.[0]?.url ? <img src={p.slides[0].url} alt="" className="h-full w-full object-cover" /> : null}</div>
                  <div className="p-2">
                    <div className="text-xs font-semibold truncate">{p.title}</div>
                    <div className="text-[10px] text-zinc-500">{(p.slides || []).length} slide{(p.slides || []).length === 1 ? "" : "s"} · {p.format}{p.status === "failed" ? " · failed" : ""}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        <SlidesEditor
        userRecordId={userRecordId}
        cfg={cfg}
        project={editing.id ? editing : null}
        onClose={() => { setEditing(null); setSavedAt(null); }}
        onSaved={(proj, silent) => { setItems((arr) => (arr.some((x) => x.id === proj.id) ? arr.map((x) => (x.id === proj.id ? proj : x)) : [proj, ...arr])); if (!silent) { setSavedAt(new Date()); window.scrollTo({ top: 0, behavior: "smooth" }); } }}
        onRendering={(proj) => { setItems((arr) => (arr.some((x) => x.id === proj.id) ? arr.map((x) => (x.id === proj.id ? proj : x)) : [proj, ...arr])); setEditing(null); setSavedAt(null); }}
        onPoints={(balance) => setCfg((c) => (c && c.points ? { ...c, points: { ...c.points, balance } } : c))}
      />
      </div>
    );
  }

  return (
    <div data-testid="slides-tab" className="space-y-4">
      {hero}

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : items.length === 0 ? (
        <div className="py-12 text-center text-sm text-zinc-500 rounded-2xl border border-dashed border-zinc-200 bg-white" data-testid="slides-empty">
          No videos yet — tap <b>New video</b> to build your first one in a couple of minutes.
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="slides-list">
          {items.map((p) => (
            <li key={p.id} className="rounded-2xl bg-white border border-zinc-200 overflow-hidden flex flex-col" data-testid={`slides-card-${p.id}`}>
              <div className={`relative bg-zinc-900 w-full ${p.format === "1:1" ? "aspect-square" : p.format === "16:9" ? "aspect-video" : "aspect-[9/16] max-h-[360px] mx-auto"}`} data-testid={`slides-preview-${p.id}`}>
                <div className="h-full w-full">
                {p.status === "ready" && p.video_url ? (
                  <video src={p.video_url} poster={p.thumbnail_url} controls preload="none" className="h-full w-full object-contain" data-testid={`slides-video-${p.id}`} />
                ) : (p.branding?.opening_image_url || p.slides?.find((s) => !/\.(mp4|mov|webm)$/i.test(s.url || ""))?.url || p.slides?.[0]?.url) ? (
                  <img src={p.branding?.opening_image_url || p.slides?.find((s) => !/\.(mp4|mov|webm)$/i.test(s.url || ""))?.url || p.slides[0].url} alt="" className="h-full w-full object-cover" data-testid={`slides-thumb-${p.id}`} />
                ) : <div className="h-full w-full flex items-center justify-center"><Film size={28} className="text-zinc-500" /></div>}
                </div>
                {p.status === "rendering" && (
                  <div className="absolute inset-x-0 bottom-0 bg-black/70 text-white text-[11px] px-3 py-2" data-testid={`slides-progress-${p.id}`}>
                    <div className="flex justify-between items-center gap-2">
                      <span className="truncate">{p.progress?.stage || "Rendering"}</span>
                      <span className="inline-flex items-center gap-2 shrink-0">
                        {p.progress?.pct || 0}%
                        <button type="button" onClick={() => cancelRender(p)} disabled={busyId === p.id || p.progress?.stage === "Cancelling…"} className="rounded-full bg-rose-600 hover:bg-rose-500 text-white px-2 py-0.5 inline-flex items-center gap-1 disabled:opacity-60" data-testid={`slides-cancel-${p.id}`}>
                          {busyId === p.id ? <Loader2 size={10} className="animate-spin" /> : <Square size={9} fill="currentColor" />} Cancel render
                        </button>
                      </span>
                    </div>
                    <div className="h-1 rounded-full bg-white/20 mt-1 overflow-hidden"><div className="h-full bg-[#c9a34a] transition-[width] duration-700" style={{ width: `${p.progress?.pct || 0}%` }} /></div>
                  </div>
                )}
                {p.status === "failed" && <div className="absolute inset-x-0 bottom-0 bg-rose-600/90 text-white text-[11px] px-3 py-1.5" data-testid={`slides-error-${p.id}`}>{p.error || "Render failed"}</div>}
                <span className="absolute top-2 left-2 rounded-full bg-black/60 text-white text-[10px] px-2 py-0.5">{p.format}{p.duration_s ? ` · ${fmtDur(p.duration_s)}` : ""}</span>
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-zinc-900 truncate">{p.title}</div>
                  <div className="text-[11px] text-zinc-500">{(p.slides || []).length} slide{(p.slides || []).length === 1 ? "" : "s"} · {p.script_mode === "per_slide" ? "narration per slide" : "one narration"} · {p.status}</div>
                </div>
                <div className="flex flex-wrap gap-1.5 text-[11px] mt-auto">
                  <button type="button" onClick={() => setEditing(p)} disabled={p.status === "rendering"} className="rounded-full px-2.5 py-1 bg-zinc-100 text-zinc-700 inline-flex items-center gap-1 disabled:opacity-50" data-testid={`slides-edit-${p.id}`}><Pencil size={11} /> Edit</button>
                  {p.status === "ready" && p.video_url && <a href={p.video_url} target="_blank" rel="noreferrer" className="rounded-full px-2.5 py-1 bg-zinc-100 text-zinc-700 inline-flex items-center gap-1" data-testid={`slides-open-${p.id}`}><Play size={11} /> Open</a>}
                  {p.status === "ready" && p.video_url && <a href={p.video_url} download={`${(p.title || "video").replace(/[^\w.-]+/g, "-")}.mp4`} className="rounded-full px-2.5 py-1 bg-zinc-100 text-zinc-700 inline-flex items-center gap-1" title="Download the MP4" data-testid={`slides-download-${p.id}`}><Download size={11} /> Download</a>}
                  {p.status === "ready" && p.media_video_id && <button type="button" onClick={() => reel(p)} disabled={busyId === p.id} className="rounded-full px-2.5 py-1 bg-pink-50 text-pink-700 inline-flex items-center gap-1" data-testid={`slides-reel-${p.id}`}><Instagram size={11} /> Post Reel</button>}
                  {p.status === "ready" && p.airtable_post_id && <button type="button" onClick={() => shareToPost(p)} disabled={busyId === p.id} className="rounded-full px-2.5 py-1 bg-violet-50 text-violet-700 inline-flex items-center gap-1 disabled:opacity-50" title="Create a post with this video — then push it to your blog, home page or Instagram" data-testid={`slides-share-post-${p.id}`}>{busyId === p.id ? <Loader2 size={11} className="animate-spin" /> : <Share2 size={11} />} Share to Post</button>}
                  {p.status === "ready" && p.media_video_id && !isAdmin && <button type="button" onClick={() => sendToCrm(p)} className="rounded-full px-2.5 py-1 bg-amber-50 text-amber-900 inline-flex items-center gap-1" title="Pick contacts in your CRM — the video link is pre-filled in the SMS / email" data-testid={`slides-send-crm-${p.id}`}><Send size={11} /> Send by SMS/email</button>}
                  {p.status === "ready" && p.media_video_id && isAdmin && (<>
                    <button type="button" onClick={() => quickShare(p, "email")} className="rounded-full px-2.5 py-1 bg-amber-50 text-amber-900 inline-flex items-center gap-1" title="Open your email app with the video link" data-testid={`slides-send-email-${p.id}`}><Mail size={11} /> Email</button>
                    <button type="button" onClick={() => quickShare(p, "sms")} className="rounded-full px-2.5 py-1 bg-amber-50 text-amber-900 inline-flex items-center gap-1" title="Open your SMS app with the video link" data-testid={`slides-send-sms-${p.id}`}><MessageSquare size={11} /> Text</button>
                  </>)}
                  {p.status === "ready" && p.airtable_post_id && <span className="rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 inline-flex items-center gap-1" title="Saved to the Media library / Posts table"><FileText size={11} /> In Media</span>}
                  <button type="button" onClick={() => duplicate(p)} disabled={busyId === p.id || p.status === "rendering"} className="rounded-full px-2.5 py-1 bg-zinc-100 text-zinc-700 inline-flex items-center gap-1 disabled:opacity-50" title="Copy this video's slides, script and branding into a new draft" data-testid={`slides-duplicate-${p.id}`}><Copy size={11} /> Duplicate</button>
                  {canPushLive && p.status !== "rendering" && <button type="button" onClick={() => pushLive(p)} disabled={busyId === p.id} className="rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-800 inline-flex items-center gap-1 disabled:opacity-50" title="Copy this project to the live portal (Slides + Media library) instead of rebuilding it there" data-testid={`slides-push-live-${p.id}`}><Rocket size={11} /> Push to live</button>}
                  <button type="button" onClick={() => remove(p)} disabled={busyId === p.id || p.status === "rendering"} className="rounded-full px-2.5 py-1 bg-rose-50 text-rose-700 inline-flex items-center gap-1 ml-auto disabled:opacity-50" data-testid={`slides-delete-${p.id}`}><Trash2 size={11} /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
